import React, { useEffect, useState } from "react";
import { Star, Plus, Trash2, Loader2, Send, RotateCcw, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { reputationApi } from "../../lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function StarRow({ count }) {
  return (
    <div className="flex items-center gap-0.5" data-testid="rep-star-row">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`h-3.5 w-3.5 ${n <= count ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
      ))}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "\u2014" : d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function EmailPreviewPanel({ branding }) {
  const companyName = branding?.companyName || "Your Company";
  const rawLogo = branding?.logoDataUrl;
  const logoSrc = rawLogo ? (rawLogo.startsWith("/api/static/") ? `${BACKEND}${rawLogo}` : rawLogo) : null;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="rep-email-preview-panel">
      <div className="mx-auto max-w-[300px] rounded-md border-2 border-indigo-100 bg-gradient-to-b from-slate-50 to-indigo-50 p-6 text-center shadow-sm">
        {logoSrc ? (
          <img src={logoSrc} alt={companyName} data-testid="rep-preview-logo" className="mx-auto mb-3 h-9 max-w-[160px] object-contain" />
        ) : (
          <div className="mb-3 text-[15px] font-bold text-slate-800" data-testid="rep-preview-company-name">{companyName}</div>
        )}
        <p className="mb-4 text-[12.5px] italic leading-relaxed" style={{ color: "#173B89" }}>
          {companyName} would like to know how we did? Your feedback is extremely important to us.
        </p>
        <div className="mb-4 flex items-center justify-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} className="h-5 w-5 text-slate-300" />
          ))}
        </div>
        <div className="inline-block rounded-md bg-blue-700 px-6 py-2 text-[12px] font-bold text-white">Rate Us</div>
      </div>
      <p className="mt-3 text-center text-[11px] text-slate-400">
        Customers who rate 3 stars or less will see the feedback form. 4-5 star ratings are routed to your public review link.
      </p>
    </div>
  );
}

export default function ReputationManagementTab({ onOpenCustomer, onOpenJob, branding }) {
  const [settings, setSettings] = useState({ alertEmail: "", sendOnJobCompletion: false, questions: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [responses, setResponses] = useState([]);
  const [manualEmail, setManualEmail] = useState("");
  const [manualSending, setManualSending] = useState(false);
  const [manualResult, setManualResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([reputationApi.getSettings(), reputationApi.listResponses()]);
      setSettings({ alertEmail: s.alertEmail || "", sendOnJobCompletion: !!s.sendOnJobCompletion, questions: s.questions || [] });
      setResponses(Array.isArray(r) ? r : []);
    } catch (_) {
      /* keep defaults */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addQuestion = () => setSettings((s) => ({ ...s, questions: [...s.questions, { id: `new-${Date.now()}`, label: "" }] }));
  const removeQuestion = (id) => setSettings((s) => ({ ...s, questions: s.questions.filter((q) => q.id !== id) }));
  const updateQuestion = (id, label) => setSettings((s) => ({ ...s, questions: s.questions.map((q) => (q.id === id ? { ...q, label } : q)) }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await reputationApi.updateSettings({
        alertEmail: settings.alertEmail,
        sendOnJobCompletion: settings.sendOnJobCompletion,
        questions: settings.questions.filter((q) => (q.label || "").trim()),
      });
      setSettings({ alertEmail: updated.alertEmail || "", sendOnJobCompletion: !!updated.sendOnJobCompletion, questions: updated.questions || [] });
      toast.success("Reputation Management settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendManual = async () => {
    const email = manualEmail.trim();
    if (!email) return;
    setManualSending(true);
    setManualResult(null);
    try {
      const res = await reputationApi.sendManualRequest(email);
      if (res.sent) {
        setManualResult({ ok: true, message: "Sent successfully" });
        setManualEmail("");
      } else {
        const reasonMap = { email_not_configured: "Email provider isn't configured yet.", demo_sandbox: "Emails are disabled in the demo workspace.", no_email: "Please enter an email address." };
        setManualResult({ ok: false, message: reasonMap[res.reason] || res.reason || "Failed to send email." });
      }
    } catch (e) {
      setManualResult({ ok: false, message: e?.response?.data?.detail || "Failed to send email." });
    } finally {
      setManualSending(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="reputation-tab">
      {/* Settings + form builder */}
      <div className="bg-white rounded-xl border border-slate-200/80 p-4 space-y-5">
        <h3 className="text-[14px] font-semibold text-slate-900">Reputation Management settings</h3>

        <div>
          <label className="block text-[12.5px] font-semibold text-slate-700 mb-1.5">Send review alert email to:</label>
          <input
            type="email"
            value={settings.alertEmail}
            onChange={(e) => setSettings((s) => ({ ...s, alertEmail: e.target.value }))}
            placeholder="alerts@yourcompany.com"
            data-testid="rep-alert-email-input"
            className="w-full max-w-sm h-9 px-3 rounded-md border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          />
          <p className="text-[11.5px] text-slate-400 mt-1.5">
            Notified whenever a customer submits 3 stars or less, with the feedback form results attached.
          </p>
        </div>

        <div className="flex items-center justify-between max-w-md bg-slate-50 rounded-lg px-3.5 py-3">
          <div className="min-w-0 pr-3">
            <div className="text-[13px] font-semibold text-slate-800">Send Review Request on Job Completion</div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">Auto-emails the customer on file the moment a job's status changes to Completed.</div>
          </div>
          <button
            type="button"
            onClick={() => setSettings((s) => ({ ...s, sendOnJobCompletion: !s.sendOnJobCompletion }))}
            data-testid="rep-auto-send-toggle"
            role="switch"
            aria-checked={settings.sendOnJobCompletion}
            className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${settings.sendOnJobCompletion ? "bg-blue-600" : "bg-slate-300"}`}
          >
            <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${settings.sendOnJobCompletion ? "translate-x-5" : ""}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[12.5px] font-semibold text-slate-700">Feedback form</label>
              <Button onClick={addQuestion} data-testid="rep-add-question-btn" variant="outline" className="h-8 gap-1.5 text-[12.5px]">
                <Plus className="h-3.5 w-3.5" /> Add Question
              </Button>
            </div>
            <p className="text-[11.5px] text-slate-400 mb-2.5">
              Shown to customers who rate 3 stars or less. Sent by email only — no limit on the number of questions.
            </p>
            <div className="space-y-2.5">
              {settings.questions.length === 0 && (
                <div className="text-[12.5px] text-slate-400 italic border border-dashed border-slate-200 rounded-lg p-3">
                  No questions yet — click "Add Question" above.
                </div>
              )}
              {settings.questions.map((q, idx) => (
                <div key={q.id} className="border border-slate-200 rounded-lg p-3" data-testid={`rep-question-${idx}`}>
                  <div className="flex items-center gap-2">
                    <input
                      value={q.label}
                      onChange={(e) => updateQuestion(q.id, e.target.value)}
                      placeholder={`Question ${idx + 1}`}
                      data-testid={`rep-question-label-${idx}`}
                      className="flex-1 h-8 px-2.5 rounded-md border border-slate-200 text-[13px] font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    />
                    <button
                      onClick={() => removeQuestion(q.id)}
                      data-testid={`rep-remove-question-${idx}`}
                      title="Remove question"
                      className="h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center shrink-0"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <textarea
                    disabled
                    placeholder="Customer's answer will appear here…"
                    data-testid={`rep-question-answer-preview-${idx}`}
                    className="mt-2 w-full h-16 px-2.5 py-2 rounded-md border border-dashed border-slate-200 text-[12.5px] text-slate-400 bg-slate-50 resize-none"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[12.5px] font-semibold text-slate-700 mb-2">Email Preview</label>
            <EmailPreviewPanel branding={branding} />

            <div className="mt-4 border border-slate-200 rounded-lg p-3.5" data-testid="rep-manual-send-section">
              <div className="text-[13px] font-semibold text-slate-800 mb-1">Send Manual Request</div>
              <p className="text-[11.5px] text-slate-500 mb-2.5">
                Send a review request email directly to any address — no job needs to be associated.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  value={manualEmail}
                  onChange={(e) => { setManualEmail(e.target.value); setManualResult(null); }}
                  placeholder="customer@example.com"
                  data-testid="rep-manual-email-input"
                  className="flex-1 h-9 px-3 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
                <Button
                  onClick={sendManual}
                  disabled={manualSending || !manualEmail.trim()}
                  data-testid="rep-manual-send-btn"
                  className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                >
                  {manualSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send
                </Button>
              </div>
              {manualResult && (
                <div
                  data-testid="rep-manual-send-result"
                  className={`mt-2 flex items-center gap-1.5 text-[12.5px] font-medium ${manualResult.ok ? "text-emerald-600" : "text-rose-600"}`}
                >
                  {manualResult.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                  {manualResult.message}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-1 border-t border-slate-100 -mx-4 px-4 -mb-4 pb-4 pt-3.5">
          <Button onClick={save} disabled={saving} data-testid="rep-save-settings-btn" className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save Changes
          </Button>
        </div>
      </div>

      {/* Log */}
      <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-[14px] font-semibold text-slate-900">Reputation responses</h3>
          <div className="text-[12px] text-slate-400">{responses.length} total</div>
        </div>
        {responses.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-slate-400" data-testid="rep-responses-empty">
            No reputation responses yet. They'll show up here once a customer rates a completed job.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="rep-responses-table">
              <thead>
                <tr className="border-b border-slate-100 text-slate-500">
                  <th className="text-left font-semibold px-4 py-2.5">Customer Name</th>
                  <th className="text-left font-semibold px-4 py-2.5">Job Number</th>
                  <th className="text-left font-semibold px-4 py-2.5">Job Completed Date</th>
                  <th className="text-left font-semibold px-4 py-2.5">Review Submitted Date</th>
                  <th className="text-left font-semibold px-4 py-2.5">Technician Name</th>
                  <th className="text-left font-semibold px-4 py-2.5">Star Count</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {responses.map((r) => (
                  <tr key={r.id} data-testid={`rep-response-row-${r.id}`} className="hover:bg-slate-50/70">
                    <td className="px-4 py-2.5">
                      {r.customerId ? (
                        <button onClick={() => onOpenCustomer?.(r.customerId)} data-testid={`rep-open-customer-${r.id}`} className="font-semibold text-blue-700 hover:underline">
                          {r.customerName}
                        </button>
                      ) : (
                        <span className="font-medium text-slate-700">{r.customerName}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {r.jobId ? (
                        <button onClick={() => onOpenJob?.(r.jobId)} data-testid={`rep-open-job-${r.id}`} className="font-semibold text-blue-700 hover:underline">
                          {r.jobNumber}
                        </button>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{fmtDate(r.jobCompletedDate)}</td>
                    <td className="px-4 py-2.5 text-slate-600">{fmtDate(r.reviewSubmittedDate)}</td>
                    <td className="px-4 py-2.5 text-slate-600">{r.technicianName || "—"}</td>
                    <td className="px-4 py-2.5"><StarRow count={r.starCount} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
